import type { ProcessedTshirtImage } from "../../../shared/image-processing";
import type { TshirtMetadata, TshirtSide } from "../../../shared/tshirts";
import { createClientId } from "../identity/create-id";

export type CollectionItem = {
  readonly id: string;
  readonly name: string;
  readonly images: Partial<Record<TshirtSide, CollectionImage>>;
  readonly createdAt: string;
};

export type CollectionImage = {
  readonly side: TshirtSide;
  readonly imageUrl: string;
  readonly processedImage?: ProcessedTshirtImage;
};

export type CaptureStatus = "idle" | "processing" | "succeeded" | "failed";
export type CollectionStatus = "idle" | "loading" | "succeeded" | "failed";

export type AppState = {
  readonly items: readonly CollectionItem[];
  readonly collection: {
    readonly status: CollectionStatus;
    readonly message: string | null;
  };
  readonly capture: {
    readonly status: CaptureStatus;
    readonly message: string | null;
  };
};

export type AppAction =
  | {
      readonly type: "image-processing-started";
    }
  | {
      readonly type: "image-processing-progress";
      readonly message: string;
    }
  | {
      readonly type: "image-processing-failed";
      readonly message: string;
    }
  | {
      readonly type: "image-preview-created";
    }
  | {
      readonly type: "collection-loading-started";
    }
  | {
      readonly type: "collection-loading-succeeded";
      readonly items: readonly CollectionItem[];
    }
  | {
      readonly type: "collection-loading-failed";
      readonly message: string;
    }
  | {
      readonly type: "image-added";
      readonly image: ProcessedTshirtImage;
      readonly now: string;
      readonly tshirt?: TshirtMetadata;
      readonly saved: boolean;
    };

export const initialAppState: AppState = {
  items: [],
  collection: {
    status: "idle",
    message: null,
  },
  capture: {
    status: "idle",
    message: null,
  },
};

const buildCollectionItem = (
  image: ProcessedTshirtImage,
  now: string,
  tshirt?: TshirtMetadata,
): CollectionItem => ({
  id: tshirt?.id ?? createClientId(),
  name: tshirt?.name ?? "名称未設定",
  images: {
    [image.side]: {
      side: image.side,
      imageUrl: image.objectUrl,
      processedImage: image,
    },
  },
  createdAt: tshirt?.createdAt ?? now,
});

const createImageAddedMessage = (image: ProcessedTshirtImage): string =>
  image.segmentationSource === "mediapipe"
    ? `服の輪郭に沿って切り抜き、コレクションに追加しました（マスク ${(image.segmentationOpaqueRatio * 100).toFixed(1)}%）`
    : "ガイド型で切り抜き、コレクションに追加しました";

const createImagePreviewMessage = (): string =>
  "プレビューを作成しました。保存するにはGoogleでログインしてください。";

const mergeCollectionItems = (params: {
  readonly currentItems: readonly CollectionItem[];
  readonly loadedItems: readonly CollectionItem[];
}): readonly CollectionItem[] => [
  ...params.currentItems.filter((currentItem) =>
    params.loadedItems.every((loadedItem) => loadedItem.id !== currentItem.id),
  ),
  ...params.loadedItems,
];

export const appReducer = (state: AppState, action: AppAction): AppState =>
  action.type === "image-processing-started"
    ? {
        ...state,
        capture: {
          status: "processing",
          message: "画像を読み込んでいます",
        },
      }
    : action.type === "image-processing-progress"
      ? {
          ...state,
          capture:
            state.capture.status === "processing"
              ? {
                  status: "processing",
                  message: action.message,
                }
              : state.capture,
        }
      : action.type === "image-processing-failed"
        ? {
            ...state,
            capture: {
              status: "failed",
              message: action.message,
            },
          }
        : action.type === "image-preview-created"
          ? {
              ...state,
              capture: {
                status: "succeeded",
                message: createImagePreviewMessage(),
              },
            }
          : action.type === "collection-loading-started"
            ? {
                ...state,
                collection: {
                  status: "loading",
                  message: "保存済みTシャツを読み込んでいます",
                },
              }
            : action.type === "collection-loading-succeeded"
              ? {
                  ...state,
                  items: mergeCollectionItems({
                    currentItems: state.items,
                    loadedItems: action.items,
                  }),
                  collection: {
                    status: "succeeded",
                    message: null,
                  },
                }
              : action.type === "collection-loading-failed"
                ? {
                    ...state,
                    collection: {
                      status: "failed",
                      message: action.message,
                    },
                  }
                : {
                    ...state,
                    items: [
                      buildCollectionItem(
                        action.image,
                        action.now,
                        action.tshirt,
                      ),
                      ...state.items,
                    ],
                    collection: {
                      status: "succeeded",
                      message: null,
                    },
                    capture: {
                      status: "succeeded",
                      message: action.saved
                        ? createImageAddedMessage(action.image)
                        : createImagePreviewMessage(),
                    },
                  };
